import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, InputNumber, Tag, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReloadOutlined } from '@ant-design/icons';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useCurrentStudyYear } from '../../hooks/useStudyYear';
import { StudyYearDropdown } from '../StudyYearDropdown';
import type { ExcelSummary } from '../../Types/interfaces';
import { Roles, StepName } from '../../Types/interfaces';
import {
  GetSummaryOfInternshipProcessesOfUser,
  ReGradeEvaluationReport,
} from '../../helper/apiHelper';
import type { SummaryColumn } from './summaryTableShared';
import {
  SummaryTable,
  listFilterProps,
  numberFilterProps,
  useColumnSearch,
  useTranslatedLabel,
} from './summaryTableShared';

type Row = ExcelSummary;
const EMPTY_ROWS: Row[] = [];

// Grades of 0 mean "not graded yet"
const gradeLabel = (grade: number): number | '' =>
  grade ? Math.round(grade * 100) / 100 : '';

// The status column shows the step the process is at, translated with the
// same texts as the process page
const STEP_TRANSLATION_KEYS: Record<string, string> = {
  [StepName.internshipProcessRequested]: 'internshipProcessComp.accepting',
  [StepName.allocatingTutorFromOrganization]:
    'internshipProcessComp.assignMentor',
  [StepName.allocatingUniversitySupervisorFromUBB]:
    'internshipProcessComp.selectSupervisor',
  [StepName.workSafetyInstructionReading]:
    'internshipProcessComp.worksafetyInstruction',
  [StepName.routeStatementFilling]: 'internshipProcessComp.routeStatement',
  [StepName.activityDeclarationFromUBBFilling]:
    'internshipProcessComp.universityActivityDeclaration',
  [StepName.activityDeclarationFromOrganizationFilling]:
    'internshipProcessComp.organizationActivityDeclaration',
  [StepName.labourProtectionApproving]:
    'internshipProcessComp.approveLabourProtection',
  [StepName.conventionFillingAndSigningByStudent]:
    'internshipProcessComp.fillConventionByStudent',
  [StepName.signingInternshipConventionByOrganization]:
    'internshipProcessComp.signConventionByOrganization',
  [StepName.signingInternshipConventionByTutor]:
    'internshipProcessComp.signConventionByTutor',
  [StepName.signingInternshipConventionByUniversitySupervisor]:
    'internshipProcessComp.signConventionBySupervisor',
  [StepName.signingInternshipConventionByRector]:
    'internshipProcessComp.signConventionByRector',
  [StepName.registeringConventionDocument]:
    'internshipProcessComp.registeringConvention',
  [StepName.workReportFilling]: 'internshipProcessComp.fillWorkReport',
  [StepName.validateWorkReportByTutor]:
    'internshipProcessComp.validateWorkReportByTutor',
  [StepName.signingWorkReportByStudent]:
    'internshipProcessComp.signWorkReportByStudent',
  [StepName.signingWorkReportByTutor]:
    'internshipProcessComp.signWorkReportByTutor',
  [StepName.completingEvaluationReportByOrganization]:
    'internshipProcessComp.completeEvaluationByOrganization',
  [StepName.signingEvaluationReportByTutor]:
    'internshipProcessComp.signEvaluationReportByTutor',
  [StepName.gradingWorkReportByUniversitySupervisor]:
    'internshipProcessComp.gradeWorkReportBySupervisor',
  [StepName.finalizingGradeByUniversitySupervisor]:
    'internshipProcessComp.finalizeGradeBySupervisor',
};

interface InternshipSummaryTableProps {
  role: Roles;
}

/**
 * The summary table for one role: the same columns for every role, filled
 * with the internship processes the user can see in that role. Supervisors
 * can also edit and save their grade, as in the original table.
 */
export const InternshipSummaryTable = ({
  role,
}: InternshipSummaryTableProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const searchProps = useColumnSearch<Row>();
  const translatedLabel = useTranslatedLabel();

  const canEditGrades = role === Roles.CadruDidactic;
  const queryKey = [...QUERY_KEYS.INTERNSHIP_SUMMARY_TABLE, user.id, role];

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () => GetSummaryOfInternshipProcessesOfUser(user.id, role),
  });
  const allRows = data ?? EMPTY_ROWS;

  // undefined until the user selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const { data: currentStudyYear } = useCurrentStudyYear();
  // the dropdown shows the current study year until the user selects another one, the same is filtered here
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  const rows = useMemo(
    () => allRows.filter((row) => row.studyYearId === shownStudyYearId),
    [allRows, shownStudyYearId]
  );

  // Unsaved supervisor grades, by process id
  const [editedGrades, setEditedGrades] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);

  const displayedRows = useMemo(
    () =>
      rows.map((row) =>
        row.id in editedGrades
          ? { ...row, gradeBySupervisor: editedGrades[row.id] }
          : row
      ),
    [rows, editedGrades]
  );

  // compared with all rows, so a grade edited in another study year is saved too
  const changedGrades = useMemo(
    () =>
      Object.entries(editedGrades).filter(
        ([id, grade]) =>
          allRows.find((row) => row.id === id)?.gradeBySupervisor !== grade
      ),
    [allRows, editedGrades]
  );

  const setGrade = (id: string, grade: number | null) =>
    setEditedGrades((previous) => {
      const next = { ...previous };
      if (grade === null) {
        delete next[id]; // cleared: back to the saved grade
      } else {
        next[id] = grade;
      }
      return next;
    });

  const saveGrades = async (): Promise<void> => {
    setSaving(true);
    const results = await Promise.allSettled(
      changedGrades.map(([id, grade]) =>
        ReGradeEvaluationReport(Number(id), { finalGrade: grade })
      )
    );
    setSaving(false);

    const failedIds = changedGrades
      .filter((_, index) => results[index].status === 'rejected')
      .map(([id]) => id);

    // keep only the edits that failed, so they can be retried
    setEditedGrades((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(([id]) => failedIds.includes(id))
      )
    );
    await queryClient.invalidateQueries({ queryKey });

    if (failedIds.length === 0) {
      messageApi.success(t('summaryTable.saveSuccess'));
    } else {
      messageApi.error(
        t('summaryTable.saveError', { count: failedIds.length })
      );
    }
  };

  const columns = useMemo<SummaryColumn<Row>[]>(
    () => [
      {
        title: t('summaryTable.studentName'),
        dataIndex: 'studentName',
        key: 'studentName',
        ...searchProps('studentName'),
      },
      {
        title: t('summaryTable.studentEmail'),
        dataIndex: 'studentEmail',
        key: 'studentEmail',
        ...searchProps('studentEmail'),
      },
      {
        title: t('summaryTable.studyline'),
        dataIndex: 'studyLineName',
        key: 'studyLineName',
        ...listFilterProps(displayedRows, 'studyLineName'),
      },
      {
        title: t('summaryTable.session'),
        dataIndex: 'sessionName',
        key: 'sessionName',
        ...listFilterProps(displayedRows, 'sessionName'),
      },
      {
        title: t('summaryTable.organization'),
        dataIndex: 'organizationName',
        key: 'organizationName',
        ...listFilterProps(displayedRows, 'organizationName'),
      },
      {
        title: t('summaryTable.status'),
        dataIndex: 'status',
        key: 'status',
        // the backend sends the current step name, or "Finished" at the end
        ...listFilterProps(displayedRows, 'status', (value) =>
          STEP_TRANSLATION_KEYS[value]
            ? t(STEP_TRANSLATION_KEYS[value])
            : translatedLabel('summaryTable.processStatus')(value)
        ),
        render: (value: string) =>
          value ? (
            <Tag color={value === 'Finished' ? 'success' : 'processing'}>
              {STEP_TRANSLATION_KEYS[value]
                ? t(STEP_TRANSLATION_KEYS[value])
                : translatedLabel('summaryTable.processStatus')(value)}
            </Tag>
          ) : null,
      },
      {
        title: t('summaryTable.tutorGrade'),
        dataIndex: 'gradeByTutor',
        key: 'gradeByTutor',
        sorter: (a, b) => a.gradeByTutor - b.gradeByTutor,
        ...numberFilterProps<Row>('gradeByTutor'),
        render: (grade: number) => gradeLabel(grade),
        exportValue: (record) => gradeLabel(record.gradeByTutor),
      },
      {
        title: t('summaryTable.supGrade'),
        dataIndex: 'gradeBySupervisor',
        key: 'gradeBySupervisor',
        sorter: (a, b) => a.gradeBySupervisor - b.gradeBySupervisor,
        ...numberFilterProps<Row>('gradeBySupervisor'),
        render: (grade: number, record) =>
          canEditGrades && record.status === 'Finished' ? (
            <InputNumber
              variant="borderless"
              min={1}
              max={10}
              precision={0}
              value={grade || null}
              onChange={(value) => setGrade(record.id, value)}
            />
          ) : (
            gradeLabel(grade)
          ),
        exportValue: (record) => gradeLabel(record.gradeBySupervisor),
      },
    ],
    [displayedRows, searchProps, translatedLabel, canEditGrades, t]
  );

  return (
    <>
      {contextHolder}
      <Flex
        align="center"
        justify="start"
        gap={8}
        wrap
        style={{ marginBottom: 10 }}
      >
        <Typography.Text strong>{t('studyYear.label')}:</Typography.Text>
        <StudyYearDropdown
          value={selectedStudyYearId}
          onChange={setSelectedStudyYearId}
        />
      </Flex>
      <SummaryTable<Row>
        // a new study year resets the table: its column filters, and the filtered rows the export uses
        key={shownStudyYearId}
        rowKey="id"
        rows={displayedRows}
        columns={columns}
        isLoading={isLoading}
        exportFileName="SummaryOfStudents"
        extraActions={
          <>
            {/* reloads the data; unsaved grade changes are kept */}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isFetching && !isLoading}
            >
              {t('summaryTable.refresh')}
            </Button>
            {canEditGrades && (
              <Button
                type="primary"
                onClick={saveGrades}
                loading={saving}
                disabled={changedGrades.length === 0}
              >
                {t('summaryTable.save')}
              </Button>
            )}
          </>
        }
      />
    </>
  );
};
